/**
 * The incident queue, and the approvals queue (the same screen, filtered).
 *
 * Ordered worst-first rather than newest-first. A queue sorted by arrival puts the most recent
 * low-severity noise above the Critical that arrived an hour ago, which is exactly backwards for
 * someone deciding what to pick up.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type Incident, type PlaybookRun } from '../api'
import { useSession } from '../App'
import {
  Async,
  Card,
  Chip,
  Empty,
  Note,
  SeverityChip,
  StatusChip,
  fmtRelative,
  useAsync,
  usePoll,
  useToast,
} from '../components/ui'

interface ApprovalRow {
  run_id: string
  incident_id: string
  playbook_id: string
  channel_id: string
  awaiting: {
    Approval?: {
      node_id: string
      prompt: string
      approvers: string[]
      /** Distinct people who must sign. Two for anything that can change the whole estate. */
      required?: number
      /** Who has signed so far. */
      granted_by?: string[]
      expires_at: number
    }
  }
  /** Who started the run. They cannot approve it. */
  started_by?: string
}

export default function Incidents({ approvalsOnly = false }: { approvalsOnly?: boolean }) {
  const { me } = useSession()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [severity, setSeverity] = useState('')
  const [showClosed, setShowClosed] = useState(false)

  const incidents = useAsync<Incident[]>(
    () =>
      api.get<Incident[]>(
        `/incidents?limit=200${q ? `&q=${encodeURIComponent(q)}` : ''}${severity ? `&severity=${severity}` : ''}`,
      ),
    [me.tenant, q, severity],
  )
  usePoll(incidents.reload, 20000, !approvalsOnly)

  if (approvalsOnly) return <Approvals />

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Incidents</h1>
          <div className="sub">
            Correlated signal chains that crossed the elevation threshold. Ordered by score, so the
            thing that matters most is at the top regardless of when it arrived.
          </div>
        </div>
      </div>

      <Card
        actions={
          <>
            <input
              placeholder="Search titles…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 220 }}
              aria-label="Search incidents"
            />
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={{ width: 'auto' }} aria-label="Severity">
              <option value="">All severities</option>
              {['critical', 'high', 'medium', 'low', 'info'].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <label className="row small muted" style={{ margin: 0, gap: 5 }}>
              <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
              Show closed
            </label>
          </>
        }
        title="Queue"
        tight
      >
        <Async
          state={incidents}
          empty={
            <Empty title="Nothing in the queue">
              A single Critical signal elevates immediately; anything less needs a chain that crosses
              the threshold. Either way it lands here.
            </Empty>
          }
        >
          {(list) => {
            const rows = list.filter((i) => showClosed || (i.status !== 'closed' && i.status !== 'false_positive'))
            if (rows.length === 0) {
              return (
                <Empty title="Nothing open">
                  {list.length > 0 && 'Everything has been closed. Tick “Show closed” to review them.'}
                </Empty>
              )
            }
            return (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Severity</th>
                      <th>Incident</th>
                      <th>Status</th>
                      <th>Kill-chain stages</th>
                      <th>Signals</th>
                      <th>Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((i) => (
                      <tr key={i.id} className="clickable" onClick={() => navigate(`/incidents/${i.id}`)}>
                        <td><SeverityChip severity={i.severity} score={i.score} /></td>
                        <td>
                          <div style={{ fontWeight: 550 }}>{i.title}</div>
                          <div className="faint small truncate" title={i.rationale}>
                            {i.rationale}
                          </div>
                        </td>
                        <td><StatusChip status={i.status} /></td>
                        <td className="small muted">
                          {i.tactics.length ? (
                            <span className="row wrap" style={{ gap: 4 }}>
                              {i.tactics.map((t) => (
                                <Chip key={t} tone="outline">
                                  {t}
                                </Chip>
                              ))}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="tnum">{i.signals.length}</td>
                        <td className="small muted nowrap">{fmtRelative(i.opened_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }}
        </Async>
      </Card>
    </>
  )
}

function Approvals() {
  const { can, me } = useSession()
  const toast = useToast()
  const approvals = useAsync<ApprovalRow[]>(() => api.get<ApprovalRow[]>('/approvals'), [me.tenant])
  usePoll(approvals.reload, 10000)
  const [busy, setBusy] = useState<string | null>(null)

  function decide(row: ApprovalRow, approved: boolean) {
    const nodeId = row.awaiting?.Approval?.node_id
    if (!nodeId) return
    setBusy(row.run_id)
    api
      .post<{ awaiting?: { Approval?: { required?: number; granted_by?: string[] } } }>(
        `/runs/${row.run_id}/approve`,
        { node_id: nodeId, approved },
      )
      .then((run) => {
        const gate = run?.awaiting?.Approval
        const required = gate?.required ?? 1
        const signed = gate?.granted_by?.length ?? 0
        if (approved && signed > 0 && signed < required) {
          // A two-person gate that still needs somebody else. Saying "approved" here would be
          // wrong: nothing has happened yet, and the person walking away expects it has.
          toast('ok', `Signed — ${signed} of ${required}. Still waiting for another approver.`)
        } else {
          toast(
            'ok',
            approved ? 'Approved — the playbook continues' : 'Denied — the playbook stopped',
          )
        }
        approvals.reload()
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(null))
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Approvals</h1>
          <div className="sub">
            Playbook runs paused at an approval gate. Anything with a blast radius wider than a
            single host waits here, so an automated response cannot change a shared control without
            a person deciding. Anything that can change the whole estate needs two people, and
            whoever started a run can never be one of them.
          </div>
        </div>
      </div>

      {!can('approve_action') && (
        <div style={{ marginBottom: 14 }}>
          <Note tone="warn">
            Your role cannot grant approvals — that is a separate capability from working an
            incident, so the person who proposes a wide change is not automatically the one who
            authorizes it. You can see what is waiting.
          </Note>
        </div>
      )}

      <Async
        state={approvals}
        empty={
          <Card>
            <Empty title="Nothing waiting">No playbook run is blocked on a decision.</Empty>
          </Card>
        }
      >
        {(rows) => (
          <div className="stack">
            {rows.map((row) => {
              const a = row.awaiting?.Approval
              return (
                <Card
                  key={row.run_id}
                  title={a?.prompt ?? 'Approval required'}
                  subtitle={
                    <>
                      {row.playbook_id} · run <span className="mono">{row.run_id}</span> ·{' '}
                      <Link to={`/incidents/${row.incident_id}`}>{row.incident_id}</Link>
                    </>
                  }
                  actions={
                    can('approve_action') ? (
                      <>
                        <button className="danger sm" disabled={busy === row.run_id} onClick={() => decide(row, false)}>
                          Deny
                        </button>
                        <button className="primary sm" disabled={busy === row.run_id} onClick={() => decide(row, true)}>
                          Approve
                        </button>
                      </>
                    ) : (
                      <Link className="btn sm" to={`/incidents/${row.incident_id}`}>
                        Open incident
                      </Link>
                    )
                  }
                >
                  <div className="small muted">
                    Approvers: {a?.approvers?.join(', ') || '—'}
                    {a?.expires_at ? <> · expires {fmtRelative(a.expires_at)}</> : null}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Note tone="warn">
                      If nobody responds before this expires the run halts. It never auto-approves —
                      an absence is not consent.
                    </Note>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </Async>

      <div style={{ marginTop: 16 }}>
        <RunList />
      </div>
    </>
  )
}

function RunList() {
  const { me } = useSession()
  const runs = useAsync<PlaybookRun[]>(() => api.get<PlaybookRun[]>('/runs'), [me.tenant])
  return (
    <Card title="Recent playbook runs" tight>
      <Async state={runs} empty={<Empty title="No runs yet" />}>
        {(list) => (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Playbook</th>
                  <th>Incident</th>
                  <th>State</th>
                  <th>Steps</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {list.slice(0, 25).map((r) => (
                  <tr key={r.id}>
                    <td className="mono small">{r.id}</td>
                    <td className="small">{r.playbook_id}</td>
                    <td className="small">
                      <Link to={`/incidents/${r.incident_id}`}>{r.incident_id}</Link>
                    </td>
                    <td>
                      <Chip
                        tone={
                          r.state === 'halted'
                            ? 'danger'
                            : r.state === 'completed'
                              ? 'ok'
                              : r.state === 'awaiting_approval'
                                ? 'warn'
                                : 'neutral'
                        }
                      >
                        {r.state.replace(/_/g, ' ')}
                      </Chip>
                    </td>
                    <td className="tnum">{r.steps.length}</td>
                    <td className="small muted nowrap">{fmtRelative(r.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Async>
    </Card>
  )
}
